import { actionCreators } from './actions';
import { bindActionCreators } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { useMemo } from 'react';
import get from 'lodash/get';
import { ApplicationState } from 'app/types-store';
import { serialize as serializeSimpleForm } from 'app/infrastructure/simple-form/serialize';
import { simpleFieldForPath } from 'app/infrastructure/simple-form/simple-form';
import { SimpleFieldFactoryParameters, SimpleFormParameters } from 'app/infrastructure/simple-form/types';

const selectAreaState = (statePath: string | string[]) => (state: ApplicationState) =>
  get(state, statePath);

export function useSimpleForm(statePath: string | string[], factoryParameters?: SimpleFieldFactoryParameters, 
  simpleFormParameters?: SimpleFormParameters) {
  const dispatch = useDispatch();

  const validate = useMemo(
    () => bindActionCreators(actionCreators(statePath, simpleFormParameters?.texts).validate, dispatch),
    [JSON.stringify(statePath), dispatch, simpleFormParameters]
  );

  const {
    values,
    serialize,
    validation,
    simpleForm
  } = useSelector(
    (state: ApplicationState) => ({
      values: get(selectAreaState(statePath)(state), 'simpleForm.editing.values'),
      serialize: serializeSimpleForm(statePath, state),
      validation: get(selectAreaState(statePath)(state), 'simpleForm.editing.validation'),
      simpleForm: get(selectAreaState(statePath)(state), 'simpleForm')
    })
  );

  const formValues = useMemo(() => Object.keys(values ?? {}).reduce((memo, key) => {
    if (values[key]) {
      memo[key] = values[key].value;
    }
    return memo;
  }, {}), [values]) as { [key: string]: unknown };

  const SimpleField = useMemo(
    () => simpleFieldForPath(statePath, factoryParameters),
    [JSON.stringify(statePath), factoryParameters]
  );

  return {
    validate,
    serialize,
    validation,
    formValues,
    simpleForm,
    SimpleField
  };
}
